import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { formatOrderOption } from '@/utils/orderFormatLine';
import LabeledField from './LabeledField';

type OrderTextValue =
  | string
  | number
  | { code?: string | number; name?: string; label?: string }
  | null
  | undefined;

export interface OrderAutocompleteItem {
  id: string | number;
  code?: OrderTextValue;
  order_code?: OrderTextValue;
  order_no?: OrderTextValue;
  customer_name?: OrderTextValue;
  customer?: OrderTextValue;
  total_amount?: string | number | null;
}

export interface OrderAutocompleteOption {
  id: string;
  label: string;
  displayLabel: string;
  order: OrderAutocompleteItem;
  statusColor?: string;
  line1Color?: string;
}

export interface OrderAutocompleteProps {
  orders?: OrderAutocompleteItem[];
  value?: string | null;
  onChange: (orderId: string | null, order?: OrderAutocompleteItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  loading?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  filterOptions?: (options: OrderAutocompleteOption[]) => OrderAutocompleteOption[];
  noOptionsText?: React.ReactNode;
  sx?: any;
  variant?: 'standard' | 'outlined' | 'filled';
  error?: boolean;
  helperText?: React.ReactNode;
  showCustomerName?: boolean;
}

export interface OrderFieldProps extends OrderAutocompleteProps {
  label?: string | null;
  required?: boolean;
  labelWidth?: string | number;
  fieldAlign?: 'center' | 'start';
  labelPosition?: 'top' | 'center' | 'bottom';
  wrapperSx?: any;
  endAdornment?: React.ReactNode;
}

const formatTextValue = (value: OrderTextValue): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);

  const code = typeof value.code === 'number' ? String(value.code) : value.code;
  return code || value.name || value.label || '';
};

const buildOrderOption = (
  order: OrderAutocompleteItem,
  showCustomerName: boolean,
): OrderAutocompleteOption => {
  const code =
    formatTextValue(order.code) ||
    formatTextValue(order.order_code) ||
    formatTextValue(order.order_no) ||
    String(order.id);
  const formatted = formatOrderOption({ ...order, code } as any);

  return {
    id: String(order.id),
    label: code,
    displayLabel: showCustomerName ? formatted.displayLabel : code,
    order,
    statusColor: formatted.statusColor,
    line1Color: formatted.line1Color,
  };
};

export const OrderAutocomplete: React.FC<OrderAutocompleteProps> = ({
  orders = [],
  value = null,
  onChange,
  placeholder = '',
  disabled = false,
  fullWidth = true,
  size = 'small',
  loading = false,
  inputValue,
  onInputChange,
  filterOptions,
  noOptionsText,
  sx,
  variant,
  error = false,
  helperText,
  showCustomerName = true,
}) => {
  const orderOptions = React.useMemo(
    () => orders.map((order) => buildOrderOption(order, showCustomerName)),
    [orders, showCustomerName],
  );
  const selectedOption = React.useMemo(
    () => orderOptions.find((option) => option.id === String(value)) || null,
    [orderOptions, value],
  );
  const displayInputValue =
    inputValue !== undefined && inputValue !== ''
      ? inputValue
      : selectedOption?.displayLabel || inputValue || '';
  const selectedColor = inputValue
    ? 'inherit'
    : selectedOption?.line1Color || selectedOption?.statusColor || '#0f172a';

  return (
    <Autocomplete
      options={orderOptions}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      inputValue={displayInputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          onInputChange?.(newInputValue || '');
        }
      }}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      sx={sx}
      getOptionLabel={(option) => option.displayLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={selectedOption}
      onChange={(_, newValue) => {
        onInputChange?.('');
        onChange(newValue?.id || null, newValue?.order || null);
      }}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          fullWidth={fullWidth}
          size={size}
          variant={variant}
          error={error}
          helperText={helperText}
          sx={{
            '& .MuiInputBase-input': {
              color: selectedColor,
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component='li' {...props}>
          <Typography
            variant='body2'
            sx={{
              fontSize: '0.875rem',
              color: option.line1Color || option.statusColor || '#0f172a',
            }}
          >
            {option.displayLabel}
          </Typography>
        </Box>
      )}
    />
  );
};

const OrderField: React.FC<OrderFieldProps> = ({
  label = 'Đơn hàng',
  required = false,
  labelWidth,
  fieldAlign = 'center',
  labelPosition = 'center',
  wrapperSx,
  endAdornment,
  ...autocompleteProps
}) => {
  const control = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <OrderAutocomplete {...autocompleteProps} />
      </Box>
      {endAdornment}
    </Box>
  );

  if (!label) return control;

  return (
    <LabeledField
      label={label}
      required={required}
      labelWidth={labelWidth}
      fieldAlign={fieldAlign}
      labelPosition={labelPosition}
      sx={wrapperSx}
    >
      {control}
    </LabeledField>
  );
};

export default OrderField;


